import React, { useEffect, useState } from 'react';
import type { ClientLogo } from '../data/clientLogos';

/** Id of the shared SVG filter (rendered once by <LogoWhiteFilter />) */
export const LOGO_WHITE_FILTER = 'logo-solid-white';

/**
 * Turns any logo file solid white. The uploaded logos are grey *and* partly transparent, so simply
 * recolouring them still read as grey. This sets every colour channel to white and multiplies the
 * transparency ×4, so even faint grey strokes come out as crisp, fully opaque white.
 */
export function LogoWhiteFilter() {
  return <svg aria-hidden="true" className="absolute h-0 w-0" data-id="element-137" data-mp="2fk3z1v2ynbe4">
      <filter id={LOGO_WHITE_FILTER} colorInterpolationFilters="sRGB" data-id="element-138" data-mp="2tgr8gxc3nu0x">
        <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 4 0" data-id="element-139" data-mp="2pggrkpmrrj7a" />
      </filter>
    </svg>;
}

/** Natural aspect ratio per logo (width / height), shared so every file loads once */
const cache = new Map<string, Promise<number>>();
export function loadLogo(src: string): Promise<number> {
  let p = cache.get(src);
  if (!p) {
    p = new Promise<number>(resolve => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve(img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 3);
      img.onerror = () => resolve(3);
      img.src = src;
    });
    cache.set(src, p);
  }
  return p;
}
interface LogoMarkProps {
  logo: ClientLogo;
  className?: string;
}

/** A client logo in solid white, sized by shape so symbols and wordmarks carry about the same weight */
export function LogoMark({
  logo,
  className = ''
}: LogoMarkProps) {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    setAspect(null);
    loadLogo(logo.src).then(a => alive && setAspect(a));
    return () => {
      alive = false;
    };
  }, [logo.src]);
  return <img src={logo.src} alt={logo.name} draggable={false} className={`block w-auto max-w-full object-contain ${className}`} style={{
    visibility: aspect === null ? 'hidden' : 'visible',
    height: `${weightHeight(aspect ?? 3) * 100}%`,
    filter: `url(#${LOGO_WHITE_FILTER})`
  }} data-id="element-140" data-mp="38m9zg2iehaym" />;
}

/**
 * Optical size normalisation: compact symbols fill the slot height, wide wordmarks get shorter so
 * their area (visual weight) roughly matches.
 */
function weightHeight(aspect: number): number {
  const REF = 1.15; // aspect that fills the full slot height
  return Math.min(1, Math.max(0.3, Math.pow(REF / Math.max(aspect, 0.01), 0.42)));
}